import animateScrollTo, { IUserOptions } from 'animated-scroll-to';
import { fromEvent, merge, Observable, of, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { snapdom } from '@zumer/snapdom';

import { AppConstants } from '../config';

export function scrollToElement(target: string | number = 0, options?: IUserOptions): Observable<boolean> {
	const animComplete = new Subject<boolean>();

	const config: IUserOptions = {
		// duration of the scroll per 1000px, default 500
		speed: 500,

		// minimum duration of the scroll
		minDuration: 250,

		// maximum duration of the scroll
		maxDuration: 1500,

		// DOM element to scroll, default window
		// Pass a reference to a DOM object
		// Example: document.querySelector('#element-to-scroll'),
		elementToScroll: typeof AppConstants.ANIMATED_SCROLL_ELEMENT_DEFAULT === 'string' ?
			document.querySelector((AppConstants.ANIMATED_SCROLL_ELEMENT_DEFAULT as string)) :
			window,

		// should animated scroll be canceled on user scroll/keypress
		// if set to "false" user input will be disabled until animated scroll is complete
		// (when set to false, "passive" will be also set to "false" to prevent Chrome errors)
		cancelOnUserAction: true,

		...options,
	};

	if (typeof(target) === 'string') {
		const element = document.querySelector(target) as HTMLElement;
		if (element) {
			animateScrollTo(element, config).then(() => {
				animComplete.next(true);
				animComplete.complete();
			});
		} else {
			console.warn(`[ui] cannot scroll to "${target}": no such element in DOM`);
		}
	} else {
		animateScrollTo(target, config).then(() => {
			animComplete.next(true);
			animComplete.complete();
		});
	}

	return animComplete.asObservable();
}

export function scrollToTop(element?: string): Observable<boolean> {
	const options: any = {};
	if (element) {
		options.element = document.querySelector(element) as HTMLElement
	}
	return scrollToElement(0, options);
}

export function fromMobileScrollEvent(element?: string): Observable<Event> {
	return fromEvent(document.querySelector(element || AppConstants.ANIMATED_SCROLL_ELEMENT_MOBILE_DEFAULT), 'scroll').pipe(
		filter(event => {
			const target = event.target as HTMLElement;
			return target.scrollHeight - target.scrollTop - target.clientHeight < AppConstants.ANIMATED_SCROLL_ELEMENT_MOBILE_TRIGGER;
		}),
	)
}

export const onLine$ = merge(
	of(null),
	fromEvent(window, 'online'),
	fromEvent(window, 'offline')
).pipe(map(() => navigator.onLine))

export function screenCaptureAndDownload(element: HTMLElement, filename: string, options = {}): Promise<void> {
	const [ onCapture, offCapture ] = [
		element.querySelectorAll('.capture-only'),
		element.querySelectorAll('.capture-hidden'),
	]

	onCapture.forEach((b:HTMLButtonElement) => b.style.display = 'block')
	offCapture.forEach((b:HTMLButtonElement) => b.style.display = 'none')

	return snapdom(
		element, {
			exclude: [
				'.breadcrumb',      // dont need it
				'.btn-group',       // dont need it
				'btn-export-group', // dont need it
				'btn-group-switch', // looks awful
				'.legend-title',    // looks awful
			],
			...options
		})
		.then(c => c.download({
			format: 'png',
			filename
		}))
		.then(_ => {
			onCapture.forEach((b:HTMLButtonElement) => b.style.display = 'none')
			offCapture.forEach((b:HTMLButtonElement) => b.style.display = '')
		})
		.catch(e => {
			console.error(`[capture] error while taking capture`)
		})
}